/**
 * Top-bar program switcher + APP context chips (P7.1 — the locked mockup's
 * design, verbatim): a MIPS | APP Plus pill pair on the right of the top bar,
 * visible only when the user holds both grants and is inside a program; in
 * APP context the practice selector is replaced by ACO + Track chips.
 * (docs/reference/app-plus/UI_ALIGNMENT.md §1)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiClient } from '@/lib/api-client';
import { useProgramAccess } from '@/platform/ProgramAccessContext';
import { useActiveProgram } from '@/platform/useActiveProgram';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import userStorage from '@/lib/userStorage';

export function ProgramSwitcherPills() {
  const navigate = useNavigate();
  const { hasProgram } = useProgramAccess();
  // Session program keeps pills visible on Home / Operations so the user can
  // switch without bouncing through the Programs launcher (owner 2026-07-21).
  const context = useActiveProgram();

  if (!(hasProgram('mips') && hasProgram('app-plus'))) return null;
  if (context === 'neutral') return null;

  const pill = (label: string, active: boolean, onClick: () => void) => (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-accent'
      )}
    >
      {label}
    </button>
  );

  const enterMips = () => {
    const hasPractice = !!userStorage.getSessionItem('selectedPracticeId');
    navigate(hasPractice ? '/dashboard' : '/dashboard');
  };

  return (
    <div className="flex items-center gap-1.5">
      {pill('MIPS', context === 'mips', enterMips)}
      {pill('APP Plus', context === 'app-plus', () => navigate('/programs/app-plus'))}
    </div>
  );
}

/**
 * ACO + Track chips shown in APP context (replaces the practice selector).
 * D7: the ACO chip becomes a DROPDOWN when the client holds >1 ACO.
 */
export function AcoContextChips() {
  const { acos, selectedAco, selectAco } = useAco();
  const { programYear } = useProgramYear();
  const [track, setTrack] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTrack(null);
    if (!selectedAco) return;
    apiClient.get(`/api/acos/${selectedAco.id}/config?year=${programYear}`)
      .then(cfg => { if (!cancelled && cfg?.track) setTrack(String(cfg.track).replace('_', ' ')); })
      .catch(() => { /* chip degrades */ });
    return () => { cancelled = true; };
  }, [selectedAco?.id, programYear]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedAco) return null;
  const chip = (label: string, value: string, emphasis = false) => (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs whitespace-nowrap',
      emphasis ? 'border-primary/40 bg-primary/5 font-medium' : 'border-border text-muted-foreground'
    )}>
      <span className="text-muted-foreground">{label}:</span>
      <span className={emphasis ? 'text-foreground' : ''}>{value}</span>
    </span>
  );

  return (
    <div className="flex items-center gap-1.5 overflow-hidden">
      {acos.length > 1 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-xs font-medium whitespace-nowrap hover:bg-primary/10 transition-colors">
              <span className="text-muted-foreground">ACO:</span>
              <span className="text-foreground">{selectedAco.aco_id_cms} — {selectedAco.name}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {acos.map(a => (
              <DropdownMenuItem key={a.id} onClick={() => selectAco(a.id)}>
                <span className={cn(a.id === selectedAco.id && 'font-medium')}>
                  {a.aco_id_cms} — {a.name}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        chip('ACO', `${selectedAco.aco_id_cms} — ${selectedAco.name}`, true)
      )}
      {track && chip('Track', track)}
    </div>
  );
}
